import { useEffect, useState } from 'react';
import usePageMeta from '../utils/usePageMeta';
import pagesSeo from '../data/pagesSeo.json';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import styles from './ReadingGroupPage.module.css';
import typography from '../theme/typography';
import colors from '../theme/colors';
import readingGroupImage from '../assets/images/reading-group.png';
import { isContentfulConfigured } from '../contentful/client';
import { fetchReadingGroupPage } from '../contentful/readingGroupPageApi';

const ReadingGroupPage = () => {
  usePageMeta(pagesSeo.readingGroup);
  const [pageData, setPageData] = useState(null);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!isContentfulConfigured()) {
        return;
      }
      setStatus('loading');
      try {
        const data = await fetchReadingGroupPage();
        if (mounted) {
          setPageData(data);
          setStatus('success');
        }
      } catch (err) {
        console.error('Failed to load Reading Group page from Contentful', err);
        if (mounted) {
          setStatus('error');
        }
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const richTextOptions = {
    renderMark: {
      bold: (text) => (
        <strong style={{ fontFamily: "'Jost', sans-serif", fontSize: '18px', fontStyle: 'normal', fontWeight: 700, lineHeight: '26px', color: colors.dark }}>
          {text}
        </strong>
      ),
    },
    renderNode: {
      paragraph: (node, children) => (
        <p style={{ fontFamily: "'Jost', sans-serif", fontSize: '18px', fontStyle: 'normal', fontWeight: 400, lineHeight: '26px', color: colors.dark }}>{children}</p>
      ),
      'heading-1': (node, children) => (
        <h1 className={styles.title} style={{ fontFamily: "'Jost', sans-serif", fontSize: '28px', fontStyle: 'normal', fontWeight: 500, lineHeight: '40px', color: colors.dark }}>
          {children}
        </h1>
      ),
      'heading-2': (node, children) => (
        <h2 style={{ fontFamily: "'Jost', sans-serif", fontSize: '28px', fontStyle: 'normal', fontWeight: 500, lineHeight: '40px', color: colors.dark }}>{children}</h2>
      ),
      'heading-3': (node, children) => (
        <h3 style={{ fontFamily: "'Jost', sans-serif", fontSize: '28px', fontStyle: 'normal', fontWeight: 500, lineHeight: '40px', color: colors.dark }}>{children}</h3>
      ),
      'unordered-list': (node, children) => <ul className={styles.list}>{children}</ul>,
      'ordered-list': (node, children) => <ol className={styles.list}>{children}</ol>,
      'list-item': (node, children) => <li className={styles.listItem}>{children}</li>,
      hyperlink: (node, children) => {
        const href = node.data?.uri || '#';
        return (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className={styles.link}
            style={{ ...typography.body.large.regular, color: colors.dark }}
          >
            {children}
          </a>
        );
      },
    },
  };

  const remoteDocument = pageData?.contentDocument;
  const hasRemoteContent =
    remoteDocument?.nodeType === 'document' && Array.isArray(remoteDocument?.content);

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.container}>
        <h1 className={`${styles.title} page-primary-title`} style={{ ...typography.heading.large, color: colors.dark }}>
          READING GROUP
        </h1>

        <div className={styles.divider} />

        <div className={styles.content}>
          <div className={styles.textColumn}>
            {hasRemoteContent ? (
              documentToReactComponents(remoteDocument, richTextOptions)
            ) : (
              <>
                <p style={{ ...typography.body.large.regular, color: colors.dark }}>
                  We organize a data science reading group to read, discuss, and present research broadly related
                  to data science. For up-to-date details and correspondence related to the reading group,{' '}
                  <a
                    href="https://groups.google.com/g/dai-reading-group"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.link}
                    style={{ ...typography.body.large.regular, color: colors.dark }}
                  >
                    request to join our mailing list
                  </a>
                  . You can also email one member of the group to request to join the list if you
                  don't want to use a Google account.
                </p>

                <p style={{ ...typography.body.large.regular, color: colors.dark }}>
                  Our regular meeting schedule is<br />
                  <strong>Monday 10:00 pm - 11:00 pm</strong>
                  <br />
                  <strong>On Zoom or in D707</strong>
                </p>

                <p style={{ ...typography.body.large.regular, color: colors.dark }}>
                  We generally meet every week during the semester. Occasionally, we hold DAI Lab-member only
                  reading group sessions for draft reviews and skill sharing. These will be clearly indicated on the
                  Google calendar. All other sessions are open to anyone who is interested. Please join us!
                </p>

                <p style={{ ...typography.body.large.regular, color: colors.dark }}>
                  For up-to-date meeting information, subscribe to our calendar at right.
                </p>
              </>
            )}

            {status === 'loading' && (
              <p style={{ ...typography.body.small.regular, color: colors.gray[700] }}>Loading latest content…</p>
            )}
            {status === 'error' && (
              <p style={{ ...typography.body.small.regular, color: colors.gray[700] }}>
                Unable to load latest content right now. Showing default information.
              </p>
            )}
          </div>

          <div className={styles.imageColumn}>
            <img src={readingGroupImage} alt="Reading group illustration" className={styles.image} />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReadingGroupPage;
